import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function InspectorAccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get("t") || "", [searchParams]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [invite, setInvite] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ nome: "", email: "", password: "" });

  useEffect(() => {
    async function loadInvite() {
      if (!token) {
        setError("Invito ispettore non valido.");
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`/api/inspector/invite-info.php?t=${encodeURIComponent(token)}`, {
          credentials: "include",
        });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data?.ok) {
          throw new Error(data?.error || `HTTP ${res.status}`);
        }
        setInvite(data);
      } catch (err) {
        setError(err.message || "Invito ispettore non disponibile.");
      } finally {
        setLoading(false);
      }
    }

    loadInvite();
  }, [token]);

  function setField(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      const res = await fetch("/api/inspector/register.php", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...form, token }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
      }
      navigate(data.redirect_to || "/ispettore", { replace: true });
    } catch (err) {
      setError(err.message || "Impossibile completare la registrazione.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-10">
      <div className="mx-auto max-w-lg rounded-3xl border border-slate-200 bg-white p-6 shadow-xl">
        <div className="text-xs font-semibold uppercase tracking-wide text-sky-600">Accesso ispettore</div>
        <h1 className="mt-2 text-3xl font-bold text-slate-950">Registrazione ispettore</h1>
        <p className="mt-2 text-sm text-slate-600">
          Registrati per accedere dal telefono all'area ispettore della tua azienda.
        </p>

        {loading && <div className="mt-5 text-sm text-slate-600">Verifica invito in corso...</div>}
        {error && <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-800">{error}</div>}

        {invite && !loading && (
          <>
            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Azienda</div>
              <div className="mt-1 text-lg font-bold text-slate-950">{invite.azienda_nome || `Azienda #${invite.azienda_id}`}</div>
              <div className="mt-1 text-xs text-slate-500">Invito valido fino a {invite.expires_at}</div>
            </div>

            <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
              <label className="block">
                <span className="text-sm font-medium text-slate-700">Nome e cognome</span>
                <input
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 outline-none focus:bg-white"
                  value={form.nome}
                  onChange={(event) => setField("nome", event.target.value)}
                  required
                />
              </label>

              <label className="block">
                <span className="text-sm font-medium text-slate-700">Email</span>
                <input
                  type="email"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 outline-none focus:bg-white"
                  value={form.email}
                  onChange={(event) => setField("email", event.target.value)}
                  required
                />
              </label>

              <label className="block">
                <span className="text-sm font-medium text-slate-700">Password</span>
                <input
                  type="password"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 outline-none focus:bg-white"
                  value={form.password}
                  onChange={(event) => setField("password", event.target.value)}
                  minLength={6}
                  required
                />
              </label>

              <button
                type="submit"
                disabled={saving}
                className="w-full rounded-xl bg-slate-950 px-4 py-3 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {saving ? "Registrazione in corso..." : "Registrati e apri area ispettore"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
